/**
 * Resolve a stored selectedTheme + runtime dark-mode preference into a
 * canonical preview theme id.
 *
 * Ported from upstream/nc:components/user-portal/portal-preview.tsx:356-357
 *
 * - `cleanId`  = stored id with any legacy `-dark` suffix stripped.
 * - `previewTheme` = `cleanId` plus `-dark` when previewDarkMode is true.
 *
 * Renderers branch on `previewTheme.endsWith('-dark')` for dark styling.
 * This decouples light/dark from the theme-entry row — light and dark
 * are no longer separate rows in seedThemes() (see T1.1).
 */
export interface ResolvedTheme {
  cleanId: string;
  previewTheme: string;
  isDark: boolean;
}

export function resolveTheme(
  selectedTheme: string | null | undefined,
  previewDarkMode: boolean,
): ResolvedTheme {
  // Strip the `-dark` suffix only on KNOWN legacy ids — never silently corrupt
  // a future theme that legitimately includes "-dark" in its slug
  // (e.g. a hypothetical id "my-dark-theme"). Known legacy variants are
  // listed explicitly to prevent the unbounded regex from biting later.
  const cleanId = (selectedTheme || 'modern-teal').replace(
    /^(modern-teal|shadcn-black)-dark$/,
    '$1',
  );
  const previewTheme = previewDarkMode ? `${cleanId}-dark` : cleanId;
  return { cleanId, previewTheme, isDark: previewDarkMode };
}
